import {
  Building2,
  ChevronRight,
  Megaphone,
  Home,
  Import,
  Mail,
  ScanEye,
  Settings,
  User,
  Users,
} from "lucide-react";
import { CanAccess, useTranslate } from "ra-core";
import { Link, matchPath, useLocation } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { EmailListsPage } from "../email-lists/EmailListsPage";
import { ImportPage } from "../misc/ImportPage";
import { ProfilePage } from "../settings/ProfilePage";
import { SettingsPageMobile } from "../settings/SettingsPageMobile";
import { VisitorInsightsPage } from "../visitor-insights/VisitorInsightsPage";

type NavItem = {
  to: string;
  labelKey: string;
  fallback: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  match?: string;
};

const MAIN_NAV: NavItem[] = [
  {
    to: "/",
    labelKey: "ra.page.dashboard",
    fallback: "Dashboard",
    icon: Home,
    match: "/",
  },
  {
    to: "/contacts",
    labelKey: "resources.contacts.name",
    fallback: "Contacts",
    icon: Users,
    match: "/contacts/*",
  },
  {
    to: "/companies",
    labelKey: "resources.companies.name",
    fallback: "Companies",
    icon: Building2,
    match: "/companies/*",
  },
  {
    to: "/campaigns",
    labelKey: "resources.campaigns.name",
    fallback: "Campaigns",
    icon: Megaphone,
    match: "/campaigns/*",
  },
  {
    to: VisitorInsightsPage.path,
    labelKey: "crm.visitor_insights.nav",
    fallback: "Visitor Insights",
    icon: ScanEye,
    match: "/visitor-insights/*",
  },
  {
    to: EmailListsPage.path,
    labelKey: "crm.email_lists.nav",
    fallback: "Email lists",
    icon: Mail,
    match: "/email-lists/*",
  },
];

const ACCOUNT_NAV: NavItem[] = [
  {
    to: ProfilePage.path,
    labelKey: "crm.profile.title",
    fallback: "Profile",
    icon: User,
    match: "/profile",
  },
  {
    to: ImportPage.path,
    labelKey: "crm.header.import_data",
    fallback: "Import data",
    icon: Import,
    match: "/import",
  },
];

function isNavActive(pathname: string, item: NavItem): boolean {
  if (item.match === "/") {
    return !!matchPath("/", pathname);
  }
  if (item.match) {
    return !!matchPath(item.match, pathname);
  }
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

function MegaMenuRow({
  item,
  pathname,
  onNavigate,
}: {
  item: NavItem;
  pathname: string;
  onNavigate: () => void;
}) {
  const translate = useTranslate();
  const Icon = item.icon;
  const active = isNavActive(pathname, item);
  const label = translate(item.labelKey, {
    _: item.fallback,
    smart_count: 2,
  });

  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      className={cn(
        "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors",
        active
          ? "bg-primary/10 text-primary"
          : "hover:bg-muted text-foreground",
      )}
    >
      <Icon className="size-5 shrink-0" />
      <span className="flex-1 font-medium">{label}</span>
      <ChevronRight className="size-4 text-muted-foreground" />
    </Link>
  );
}

export const MobileMegaMenu = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const translate = useTranslate();
  const location = useLocation();

  const handleNavigate = () => onOpenChange(false);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="h-[92dvh] rounded-t-2xl p-0 flex flex-col"
        aria-label={translate("crm.mobile_nav.mega_menu_title", {
          _: "Menu",
        })}
      >
        <SheetHeader className="px-4 pt-6 pb-2 border-b shrink-0">
          <div className="flex items-center justify-between">
            <SheetTitle>
              {translate("crm.mobile_nav.mega_menu_title", { _: "Menu" })}
            </SheetTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              {translate("crm.mobile_nav.close_menu", { _: "Close" })}
            </Button>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-2 py-4">
          <p className="px-4 pb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {translate("crm.mobile_nav.section_main", { _: "Main" })}
          </p>
          <nav className="flex flex-col gap-1 mb-6">
            {MAIN_NAV.map((item) => (
              <MegaMenuRow
                key={item.to}
                item={item}
                pathname={location.pathname}
                onNavigate={handleNavigate}
              />
            ))}
          </nav>

          <p className="px-4 pb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {translate("crm.mobile_nav.section_account", { _: "Account" })}
          </p>
          <nav className="flex flex-col gap-1">
            {ACCOUNT_NAV.map((item) => (
              <MegaMenuRow
                key={item.to}
                item={item}
                pathname={location.pathname}
                onNavigate={handleNavigate}
              />
            ))}
            <MegaMenuRow
              item={{
                to: SettingsPageMobile.path,
                labelKey: "crm.settings.title",
                fallback: "Settings",
                icon: Settings,
                match: "/settings",
              }}
              pathname={location.pathname}
              onNavigate={handleNavigate}
            />
            <CanAccess resource="sales" action="list">
              <MegaMenuRow
                item={{
                  to: "/sales",
                  labelKey: "resources.sales.name",
                  fallback: "Users",
                  icon: Users,
                  match: "/sales/*",
                }}
                pathname={location.pathname}
                onNavigate={handleNavigate}
              />
            </CanAccess>
          </nav>
        </div>
      </SheetContent>
    </Sheet>
  );
};
